// src/components/terms/TermsContent.tsx
"use client";

import React from "react";
import AnimatedCard from "@/components/ui/AnimatedCard";
import { useTranslations } from "next-intl";

const SECTIONS = [
  "acceptance",
  "serviceDescription",
  "userAccounts",
  "creditsAndPayments",
  "aiDisclaimer",
  "intellectualProperty",
  "userContent",
  "acceptableUse",
  "thirdPartyRights",
  "limitationOfLiability",
  "disclaimerOfWarranties",
  "indemnification",
  "termination",
  "disputeResolution",
  "privacy",
  "generalProvisions",
  "contact",
] as const;

type SectionKey = (typeof SECTIONS)[number];

export interface TermsContentProps {
  /**
   * Modo compacto: sin hero ni tabla de contenidos, solo secciones.
   * Usado en /accept-terms para mostrar solo el contenido de T&C.
   */
  compact?: boolean;
}

/**
 * Componente reutilizable que renderiza el contenido completo de Términos y Condiciones.
 * - compact=false (default): Muestra todas las secciones con AnimatedCard
 * - compact=true: Muestra solo las secciones sin animaciones ni cards (para accept-terms)
 */
const TermsContent: React.FC<TermsContentProps> = ({ compact = false }) => {
  const t = useTranslations("terms");

  const getSectionId = (key: SectionKey): string => {
    return t(`sections.${key}.id`);
  };

  const renderList = (items: string[]) => (
    <ul className="list-disc ml-5 space-y-2 text-gray-300">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );

  const renderSectionContent = (key: SectionKey) => {
    switch (key) {
      case "acceptance":
      case "serviceDescription":
      case "userAccounts":
        return (
          <div className="text-gray-300 leading-relaxed whitespace-pre-line">
            {t(`sections.${key}.content`)}
          </div>
        );

      case "creditsAndPayments":
        return (
          <div className="space-y-6">
            <p className="text-gray-300">
              {t("sections.creditsAndPayments.contentIntro")}
            </p>

            {/* Monthly Credits */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.monthlyCredits.title")}
              </h3>
              {renderList([
                t("sections.creditsAndPayments.monthlyCredits.items.0"),
                t("sections.creditsAndPayments.monthlyCredits.items.1"),
                t("sections.creditsAndPayments.monthlyCredits.items.2"),
                t("sections.creditsAndPayments.monthlyCredits.items.3"),
              ])}
            </div>

            {/* Purchased Credits */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.purchasedCredits.title")}
              </h3>
              {renderList([
                t("sections.creditsAndPayments.purchasedCredits.items.0"),
                t("sections.creditsAndPayments.purchasedCredits.items.1"),
                t("sections.creditsAndPayments.purchasedCredits.items.2"),
                t("sections.creditsAndPayments.purchasedCredits.items.3"),
                t("sections.creditsAndPayments.purchasedCredits.items.4"),
              ])}
            </div>

            <p className="text-gray-300 font-medium">
              {t("sections.creditsAndPayments.creditTypes")}
            </p>

            {/* Consumption */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.consumption.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                {t("sections.creditsAndPayments.consumption.preset")}
              </p>
              <p className="text-gray-300 mb-2">
                {t("sections.creditsAndPayments.consumption.iteration")}
              </p>
              <p className="text-gray-400 text-sm">
                {t("sections.creditsAndPayments.consumption.restoration")}
              </p>
            </div>

            {/* Billing */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.billing.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                {t("sections.creditsAndPayments.billing.processor")}
              </p>
              <p className="text-gray-300 mb-2">
                {t("sections.creditsAndPayments.billing.currency")}
              </p>
              <p className="text-gray-300">
                {t("sections.creditsAndPayments.billing.taxes")}
              </p>
            </div>

            {/* Grace Period */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.gracePeriod.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.creditsAndPayments.gracePeriod.content")}
              </p>
            </div>

            {/* Plan Changes */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.planChanges.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                <strong>Upgrade:</strong>{" "}
                {t("sections.creditsAndPayments.planChanges.upgrade")}
              </p>
              <p className="text-gray-300 mb-2">
                <strong>Downgrade:</strong>{" "}
                {t("sections.creditsAndPayments.planChanges.downgrade")}
              </p>
              <p className="text-gray-300">
                <strong>Cancellation:</strong>{" "}
                {t("sections.creditsAndPayments.planChanges.cancellation")}
              </p>
            </div>

            {/* Refunds */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.refunds.title")}
              </h3>
              <p className="text-gray-300 mb-2">
                {t("sections.creditsAndPayments.refunds.paddleMerchant")}
              </p>
              <p className="text-gray-300 mb-2">
                {t("sections.creditsAndPayments.refunds.process")}
              </p>
              <p className="text-gray-400 text-sm">
                {t("sections.creditsAndPayments.refunds.paddleTerms")}
              </p>
            </div>

            {/* Chargebacks */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.creditsAndPayments.chargebacks.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.creditsAndPayments.chargebacks.content")}
              </p>
            </div>
          </div>
        );

      case "aiDisclaimer":
        return (
          <div className="space-y-6">
            <p className="text-gray-300">
              {t("sections.aiDisclaimer.contentIntro")}
            </p>

            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">
                {t("sections.aiDisclaimer.noGuarantees.title")}
              </h3>
              {renderList([
                t("sections.aiDisclaimer.noGuarantees.items.0"),
                t("sections.aiDisclaimer.noGuarantees.items.1"),
                t("sections.aiDisclaimer.noGuarantees.items.2"),
                t("sections.aiDisclaimer.noGuarantees.items.3"),
                t("sections.aiDisclaimer.noGuarantees.items.4"),
              ])}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                {t("sections.aiDisclaimer.aiResults.title")}
              </h3>
              {renderList([
                t("sections.aiDisclaimer.aiResults.items.0"),
                t("sections.aiDisclaimer.aiResults.items.1"),
                t("sections.aiDisclaimer.aiResults.items.2"),
                t("sections.aiDisclaimer.aiResults.items.3"),
              ])}
            </div>

            <div className="text-gray-300 leading-relaxed whitespace-pre-line">
              {t("sections.aiDisclaimer.userResponsibility")}
            </div>
          </div>
        );

      case "intellectualProperty":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.intellectualProperty.rigcraftProperty.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.intellectualProperty.rigcraftProperty.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.intellectualProperty.userProperty.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.intellectualProperty.userProperty.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.intellectualProperty.license.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.intellectualProperty.license.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">
                {t("sections.intellectualProperty.restrictions.title")}
              </h3>
              <p className="text-gray-300 mb-2">You may NOT:</p>
              {renderList([
                t("sections.intellectualProperty.restrictions.items.0"),
                t("sections.intellectualProperty.restrictions.items.1"),
                t("sections.intellectualProperty.restrictions.items.2"),
                t("sections.intellectualProperty.restrictions.items.3"),
                t("sections.intellectualProperty.restrictions.items.4"),
                t("sections.intellectualProperty.restrictions.items.5"),
              ])}
            </div>
          </div>
        );

      case "userContent":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">
              {t("sections.userContent.definition")}
            </p>
            <p className="text-gray-300">
              {t("sections.userContent.responsibility")}
            </p>
            <div className="text-gray-300 leading-relaxed whitespace-pre-line">
              {t("sections.userContent.licenseToRigcraft")}
            </div>
            <p className="text-gray-300">{t("sections.userContent.deletion")}</p>
          </div>
        );

      case "acceptableUse":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-2">
                {t("sections.acceptableUse.permitted.title")}
              </h3>
              {renderList([
                t("sections.acceptableUse.permitted.items.0"),
                t("sections.acceptableUse.permitted.items.1"),
                t("sections.acceptableUse.permitted.items.2"),
              ])}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">
                {t("sections.acceptableUse.prohibited.title")}
              </h3>

              <div className="space-y-4 ml-4">
                <div>
                  <h4 className="font-medium text-white mb-1">
                    {t("sections.acceptableUse.prohibited.serviceAbuse.title")}
                  </h4>
                  {renderList([
                    t("sections.acceptableUse.prohibited.serviceAbuse.items.0"),
                    t("sections.acceptableUse.prohibited.serviceAbuse.items.1"),
                    t("sections.acceptableUse.prohibited.serviceAbuse.items.2"),
                    t("sections.acceptableUse.prohibited.serviceAbuse.items.3"),
                  ])}
                </div>

                <div>
                  <h4 className="font-medium text-white mb-1">
                    {t("sections.acceptableUse.prohibited.commercialUse.title")}
                  </h4>
                  {renderList([
                    t("sections.acceptableUse.prohibited.commercialUse.items.0"),
                    t("sections.acceptableUse.prohibited.commercialUse.items.1"),
                    t("sections.acceptableUse.prohibited.commercialUse.items.2"),
                  ])}
                </div>

                <div>
                  <h4 className="font-medium text-white mb-1">
                    {t("sections.acceptableUse.prohibited.security.title")}
                  </h4>
                  {renderList([
                    t("sections.acceptableUse.prohibited.security.items.0"),
                    t("sections.acceptableUse.prohibited.security.items.1"),
                    t("sections.acceptableUse.prohibited.security.items.2"),
                  ])}
                </div>
              </div>
            </div>

            <p className="text-gray-300">
              {t("sections.acceptableUse.consequences")}
            </p>
          </div>
        );

      case "thirdPartyRights":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">
              {t("sections.thirdPartyRights.content")}
            </p>
            <p className="text-gray-300">
              {t("sections.thirdPartyRights.notAffiliated")}
            </p>
            <div className="text-gray-300 leading-relaxed whitespace-pre-line">
              {t("sections.thirdPartyRights.trademarks")}
            </div>
          </div>
        );

      case "limitationOfLiability":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.limitationOfLiability.monetaryLimit.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.limitationOfLiability.monetaryLimit.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.limitationOfLiability.exclusionOfDamages.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.limitationOfLiability.exclusionOfDamages.content")}
              </p>
            </div>

            <p className="text-gray-400 text-sm">
              {t("sections.limitationOfLiability.exceptions")}
            </p>
          </div>
        );

      case "disclaimerOfWarranties":
        return (
          <div className="space-y-6">
            <p className="text-gray-300 font-medium uppercase">
              {t("sections.disclaimerOfWarranties.asIs")}
            </p>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.disclaimerOfWarranties.disclaims.title")}
              </h3>
              {renderList([
                t("sections.disclaimerOfWarranties.disclaims.items.0"),
                t("sections.disclaimerOfWarranties.disclaims.items.1"),
                t("sections.disclaimerOfWarranties.disclaims.items.2"),
                t("sections.disclaimerOfWarranties.disclaims.items.3"),
                t("sections.disclaimerOfWarranties.disclaims.items.4"),
                t("sections.disclaimerOfWarranties.disclaims.items.5"),
              ])}
            </div>

            <div className="text-gray-300 leading-relaxed whitespace-pre-line">
              {t("sections.disclaimerOfWarranties.noGuarantee")}
            </div>
          </div>
        );

      case "indemnification":
        return (
          <div className="space-y-4">
            <div className="text-gray-300 leading-relaxed whitespace-pre-line">
              {t("sections.indemnification.content")}
            </div>
            <p className="text-gray-300">
              {t("sections.indemnification.procedure")}
            </p>
          </div>
        );

      case "termination":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.termination.byUser.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.termination.byUser.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.termination.byRigcraft.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.termination.byRigcraft.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">
                {t("sections.termination.effects.title")}
              </h3>
              {renderList([
                t("sections.termination.effects.items.0"),
                t("sections.termination.effects.items.1"),
                t("sections.termination.effects.items.2"),
                t("sections.termination.effects.items.3"),
              ])}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.termination.inactiveAccounts.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.termination.inactiveAccounts.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.termination.minors.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.termination.minors.content")}
              </p>
            </div>

            <p className="text-gray-400 text-sm">
              {t("sections.termination.survival")}
            </p>
          </div>
        );

      case "disputeResolution":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.disputeResolution.governingLaw.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.disputeResolution.governingLaw.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.disputeResolution.arbitration.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.disputeResolution.arbitration.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">
                {t("sections.disputeResolution.classWaiver.title")}
              </h3>
              <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                {t("sections.disputeResolution.classWaiver.content")}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.disputeResolution.optOut.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.disputeResolution.optOut.content")}
              </p>
            </div>

            <p className="text-gray-400 text-sm">
              {t("sections.disputeResolution.exceptions")}
            </p>
          </div>
        );

      case "privacy":
        return (
          <div className="space-y-6">
            <div className="text-gray-300 leading-relaxed whitespace-pre-line">
              {t("sections.privacy.content")}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.privacy.dataSummary.title")}
              </h3>
              {renderList([
                t("sections.privacy.dataSummary.items.0"),
                t("sections.privacy.dataSummary.items.1"),
                t("sections.privacy.dataSummary.items.2"),
                t("sections.privacy.dataSummary.items.3"),
              ])}
            </div>

            <p className="text-gray-400 text-sm">
              {t("sections.privacy.moreInfo")}
            </p>
          </div>
        );

      case "generalProvisions":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.severability.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.severability.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.entireAgreement.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.entireAgreement.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.noWaiver.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.noWaiver.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.assignment.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.assignment.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.headings.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.headings.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.language.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.language.content")}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {t("sections.generalProvisions.notices.title")}
              </h3>
              <p className="text-gray-300">
                {t("sections.generalProvisions.notices.content")}
              </p>
            </div>
          </div>
        );

      case "contact":
        return (
          <div className="space-y-4">
            <p className="text-gray-300">{t("sections.contact.content")}</p>
            <div className="bg-gray-800/30 border border-gray-700/50 p-4 rounded-lg">
              <p className="text-white font-medium mb-2">RigCraft</p>
              <ul className="space-y-1 text-gray-300">
                <li>
                  Legal:{" "}
                  <a
                    href={`mailto:${t("sections.contact.emails.legal")}`}
                    className="text-purple-400 hover:text-purple-300"
                  >
                    {t("sections.contact.emails.legal")}
                  </a>
                </li>
                <li>
                  Support:{" "}
                  <a
                    href={`mailto:${t("sections.contact.emails.support")}`}
                    className="text-purple-400 hover:text-purple-300"
                  >
                    {t("sections.contact.emails.support")}
                  </a>
                </li>
                <li>
                  Billing:{" "}
                  <a
                    href={`mailto:${t("sections.contact.emails.billing")}`}
                    className="text-purple-400 hover:text-purple-300"
                  >
                    {t("sections.contact.emails.billing")}
                  </a>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 text-sm">
              {t("sections.contact.optOutSubject")}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Modo compacto: solo renderiza las secciones sin wrapper ni animaciones
  if (compact) {
    return (
      <div className="space-y-8">
        {SECTIONS.map((key) => {
          const sectionId = getSectionId(key);
          const title = t(`sections.${key}.title`);

          return (
            <section key={key} id={sectionId}>
              <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
              {renderSectionContent(key)}
            </section>
          );
        })}
      </div>
    );
  }

  // Modo completo: renderiza secciones con AnimatedCard y glass effect
  return (
    <div className="space-y-8">
      {SECTIONS.map((key, index) => {
        const sectionId = getSectionId(key);
        const title = t(`sections.${key}.title`);

        return (
          <AnimatedCard key={key} delay={0.15 + index * 0.02}>
            <section id={sectionId} className="scroll-mt-8">
              <div className="relative rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-white/5 backdrop-blur-xl" />
                <div className="absolute inset-[1px] rounded-xl bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

                <div className="relative p-6 md:p-8">
                  <a
                    href={`#${sectionId}`}
                    className="group flex items-center gap-2 mb-4"
                  >
                    <h2 className="text-2xl font-bold text-white group-hover:text-purple-300 transition-colors">
                      {title}
                    </h2>
                    <span className="text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      #
                    </span>
                  </a>

                  {renderSectionContent(key)}
                </div>
              </div>
            </section>
          </AnimatedCard>
        );
      })}
    </div>
  );
};

export default TermsContent;
